/**
 * BlocActions (zone [6]) — 1 primaire + les secondaires. Lecture seule :
 * navigation (open/navigate), email (compositeur classique tracé), lien externe,
 * et — depuis l'absorption de l'aperçu ⌘K le 2026-08-11 — les actions de
 * FICHIER : « coup d'œil » plein écran et téléchargement.
 *
 * ⚠️ Le plafond du nombre de secondaires est appliqué par le BACKEND
 * (`_SECONDARY_ACTIONS_CAP`), jamais ici. Un `slice` côté écran masquerait des
 * actions sans que rien ne le dise, et les deux surfaces divergeraient en
 * silence dès que l'une afficherait une action que l'autre coupe.
 */

import { useTranslation } from 'react-i18next'
import { BTN_PRIMARY, BTN_SECONDARY, SummaryIcon } from '../primitives'
import type { SummaryAction, SummaryActions } from '../types'

interface ActionsProps {
    actions: SummaryActions
    /** Ouvre la fiche épinglée (route résolue par le registre côté appelant). */
    onOpen: () => void
    onNavigate: (href: string) => void
    onSendEmail: (target: { email: string; name: string }) => void
    /** Lecteur plein écran d'une pièce. Absent → l'action est simplement omise. */
    onQuickLook?: (fileId: string) => void
}

export function SummaryActionsBlock({
    actions,
    onOpen,
    onNavigate,
    onSendEmail,
    onQuickLook,
}: ActionsProps) {
    const { t } = useTranslation()
    const { primary, secondary } = actions

    // Une action « coup d'œil » sans lecteur monté ne mènerait nulle part : on la
    // retire plutôt que de proposer un bouton mort.
    const usable = (a: SummaryAction) => a.kind !== 'quicklook' || (!!onQuickLook && !!a.file_id)
    const primaryAction = primary && usable(primary) ? primary : null
    const secondaryActions = secondary.filter(usable)
    if (!primaryAction && secondaryActions.length === 0) return null

    const run = (a: SummaryAction) => {
        if (a.kind === 'open') return onOpen()
        if (a.kind === 'navigate' && a.href) return onNavigate(a.href)
        if (a.kind === 'email' && a.email) return onSendEmail({ email: a.email, name: a.name ?? '' })
        if (a.kind === 'quicklook' && a.file_id) return onQuickLook?.(a.file_id)
    }

    const renderBtn = (a: SummaryAction, cls: string) => {
        const label = t(a.label, a.label)
        // Lien signé (ouverture externe / téléchargement) → ancre. `download` porte
        // l'attribut du même nom : sans lui, le navigateur ouvrirait le PDF au lieu
        // de l'enregistrer, et les deux boutons feraient la même chose.
        if (a.href && (a.kind === 'external' || a.kind === 'download')) {
            return (
                <a
                    key={a.key}
                    href={a.href}
                    target={a.kind === 'external' ? '_blank' : undefined}
                    rel={a.kind === 'external' ? 'noopener noreferrer' : undefined}
                    {...(a.kind === 'download' ? { download: '' } : {})}
                    className={cls}
                >
                    <SummaryIcon name={a.icon} className="h-3.5 w-3.5" />
                    {label}
                </a>
            )
        }
        return (
            <button key={a.key} type="button" onClick={() => run(a)} className={cls}>
                <SummaryIcon name={a.icon} className="h-3.5 w-3.5" />
                {label}
            </button>
        )
    }

    return (
        <div className="mt-1 flex flex-col gap-2 border-t border-border pt-4">
            {primaryAction && renderBtn(primaryAction, BTN_PRIMARY)}
            {secondaryActions.map((a) => renderBtn(a, BTN_SECONDARY))}
        </div>
    )
}
